// Formatting rules for the financials table, shared with the note.
// Mirrors backend/api/financials.py; keep the two in step.

export const MAX_DISPLAYED_ROWS = 7;
export const MAX_SELECTED_PERIODS = 4;

// Selected periods needed for each New Create forecast
export const REQUIRED_SELECTED = { one_year: 2, two_years: 4 };

// {value:m} rows are in millions on the note, to 0dp when the row's biggest value
// is at least 1,000m and to 1dp otherwise
const MILLIONS_WHOLE_FROM = 1000000000;

const FORMAT_PATTERN = /\{value(?::(m)?(,)?(?:\.(\d)f)?(\(\))?)?\}/g;

// Split a format into { prefix, millions, precision, brackets, suffix }. Supported:
// literal text around one {value}, {value:.Nf}, {value:m} or {value:m.Nf}, each
// optionally ending in "()" (e.g. {value:m()}). "m" shows the value in millions on
// the note; without .Nf it picks 0 or 1dp from the row's size. "()" puts negative
// values in brackets, prefix and suffix included ("(£2,500m)" rather than "£-2,500m").
// Values always get thousands separators, so a "," after the colon is accepted but
// changes nothing. Throws for anything else.
export const parseFormat = (fmt) => {
  fmt = fmt || "{value}";
  const matches = [...fmt.matchAll(FORMAT_PATTERN)];
  const literal = fmt.replace(FORMAT_PATTERN, "");
  if (matches.length !== 1 || literal.includes("{") || literal.includes("}")) {
    throw new Error(
      "Use one {value}, {value:.1f}, {value:m} or {value:m.1f}, with any text around it; " +
        "end it in () for brackets on negatives, e.g. {value:m()}",
    );
  }
  const match = matches[0];
  return {
    prefix: fmt.slice(0, match.index),
    millions: Boolean(match[1]),
    precision: match[3] !== undefined ? Number(match[3]) : null,
    brackets: Boolean(match[4]),
    suffix: fmt.slice(match.index + match[0].length),
  };
};

// Multiply by 10^places without binary rounding errors
const shift = (n, places) => {
  const [mantissa, exponent = "0"] = String(n).split("e");
  return Number(`${mantissa}e${Number(exponent) + places}`);
};

// Round half away from zero, like Python's ROUND_HALF_UP on a Decimal
export const roundHalfUp = (value, places) => {
  const rounded = shift(Math.round(shift(Math.abs(value), places)), -places);
  return value < 0 && rounded !== 0 ? -rounded : rounded;
};

// Round to `precision` places, or up to 4 places with trailing zeros dropped
export const formatNumber = (value, comma = false, precision = null) => {
  const places = precision ?? 4;
  const rounded = roundHalfUp(value, places);
  let text = Math.abs(rounded).toFixed(places);
  if (precision === null && text.includes(".")) {
    text = text.replace(/0+$/, "").replace(/\.$/, "");
  }
  if (comma) {
    const [whole, fraction] = text.split(".");
    text =
      whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",") +
      (fraction !== undefined ? `.${fraction}` : "");
  }
  return (rounded < 0 ? "-" : "") + text;
};

const isMissing = (value) => value === null || value === undefined;

// Decimals for a {value:m} row: 0 if its biggest value is at least 1,000m, else 1
export const millionsPrecision = (rowValues) =>
  Math.max(0, ...rowValues.filter((v) => !isMissing(v)).map(Math.abs)) >= MILLIONS_WHOLE_FROM
    ? 0
    : 1;

// A value as shown on the note and in the portal's grid, with thousands separators;
// "-" if missing. rowValues (the values in the same row; value itself if not given)
// set the decimals of a {value:m} format without .Nf.
export const formatValue = (value, fmt, { rowValues = null } = {}) => {
  if (isMissing(value)) return "-";
  const { prefix, millions, precision: fixed, brackets, suffix } = parseFormat(fmt);
  let precision = fixed;
  if (millions) {
    precision ??= millionsPrecision(rowValues ?? [value]);
    value = shift(value, -6);
  }
  const text = formatNumber(value, true, precision);
  // formatNumber never gives "-0", so this only brackets values that stay negative
  if (brackets && text.startsWith("-")) return `(${prefix}${text.slice(1)}${suffix})`;
  return `${prefix}${text}${suffix}`;
};

// Percentage financials change in basis points
export const isBps = (fmt) => (fmt || "").trimEnd().endsWith("%");

// A stored value (decimal(20,4)) as a whole number of 0.0001s, so sums are exact
const toUnits = (value) => Math.round(shift(value, 4));

// Change from old to new: bps for percentage financials (12.5 -> 12.75 is 25bps),
// otherwise % change on |old|. null when it can't be worked out.
// One division of exact integers, so it rounds like the backend's Decimal maths.
export const change = (oldValue, newValue, fmt) => {
  if (isMissing(oldValue) || isMissing(newValue)) return null;
  const diff = toUnits(newValue) - toUnits(oldValue);
  if (isBps(fmt)) return diff / 100;
  if (oldValue === 0) return null;
  return (diff * 100) / Math.abs(toUnits(oldValue));
};

// A change rounded as it's shown: whole bps, or % to 1 place
export const roundChange = (chg, fmt) => roundHalfUp(chg, isBps(fmt) ? 0 : 1);

export const formatChange = (chg, fmt) => {
  if (isMissing(chg)) return "n/a";
  const bps = isBps(fmt);
  const text = formatNumber(chg, true, bps ? 0 : 1);
  // "+" on rises; nothing on a change that rounds to zero
  const sign = !text.startsWith("-") && text.replace(/[0.]/g, "") ? "+" : "";
  return `${sign}${text}${bps ? "bps" : "%"}`;
};

// The new value that gives change `chg` from oldValue (inverse of change())
export const applyChange = (oldValue, chg, fmt) =>
  roundHalfUp(
    isBps(fmt) ? oldValue + chg / 100 : oldValue + (Math.abs(oldValue) * chg) / 100,
    4,
  );

// "good", "bad" or null (no colour)
export const changeDirection = (oldValue, newValue, biggerIsBetter) => {
  if (isMissing(oldValue) || isMissing(newValue) || isMissing(biggerIsBetter)) return null;
  if (oldValue === newValue) return null;
  return (newValue > oldValue) === Boolean(biggerIsBetter) ? "good" : "bad";
};

// { key: "OLD" | "NEW" }: selected periods alternate OLD, NEW from the left
export const selectionLabels = (periods) => {
  const labels = {};
  periods
    .filter((p) => p.selected)
    .forEach((p, i) => {
      labels[p.key] = i % 2 === 0 ? "OLD" : "NEW";
    });
  return labels;
};

// The period's name, else its end_date year (+E for estimates), with OLD/NEW in front when selected
export const periodHeading = (period, label = null) => {
  if (period.name) return period.name;
  const text = `${String(period.end_date).slice(0, 4)}${period.value_type === "estimate" ? "E" : ""}`;
  return label ? `${label} ${text}` : text;
};
